import { useState, useEffect, useCallback, useMemo } from "react";
import { CheckCircle2, Loader2, Library, Search } from "lucide-react";
import { Input } from "./ui/input";
import { PokemonCardTile } from "./PokemonCardTile";
import { makeVariant } from "../lib/variants";
import { fetchOwnedCards } from "../lib/api";

// Vue « Cartes possédées » : TOUTE la collection, toutes extensions confondues.
// Une carte y apparaît une fois par variante possédée (on peut avoir la normale
// et la reverse), d'où `owned_variant` — même principe que WantedView.
export const OwnedCardsView = ({
  collection, conditions = {}, onStatusChange, onRemove, onCardClick,
}) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const reload = useCallback(() => {
    setLoading(true);
    fetchOwnedCards()
      .then(setCards)
      .catch(err => console.error("Cartes possédées :", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Regroupement par extension : une collection un peu fournie devient
  // illisible en une seule grille de plusieurs centaines de cartes.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const kept = q
      ? cards.filter(c => (c.name || "").toLowerCase().includes(q)
          || (c.set?.name || "").toLowerCase().includes(q))
      : cards;
    const bySet = new Map();
    kept.forEach(card => {
      const key = card.set?.id || "?";
      if (!bySet.has(key)) bySet.set(key, { name: card.set?.name || "Sans extension", cards: [] });
      bySet.get(key).cards.push(card);
    });
    return [...bySet.entries()].map(([id, g]) => ({ id, ...g }));
  }, [cards, search]);

  const shown = groups.reduce((n, g) => n + g.cards.length, 0);

  const handleVariantClick = async (cardId, variantType, currentStatus, shiftKey) => {
    if (shiftKey) {
      if (currentStatus === "wanted") await onRemove(cardId, variantType);
      else await onStatusChange(cardId, variantType, "wanted");
    } else {
      if (!currentStatus || currentStatus === "wanted") await onStatusChange(cardId, variantType, "owned");
      else await onRemove(cardId, variantType);
    }
    reload();
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-red-500" /></div>;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-1 flex items-center gap-2">
        <CheckCircle2 className="w-6 h-6 text-emerald-400" />Cartes possédées
        <span className="text-base font-normal text-zinc-500">({cards.length})</span>
      </h2>
      <p className="text-sm text-zinc-500 mb-5">
        Toute la collection, regroupée par extension. Un clic sur un badge de variante la retire.
      </p>

      {cards.length > 0 && (
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input placeholder="Filtrer par nom de carte ou d'extension..." className="search-input pl-10"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}

      {shown === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Library className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{cards.length === 0
            ? "Aucune carte possédée pour l'instant."
            : "Aucune carte ne correspond à ce filtre."}</p>
        </div>
      ) : (
        groups.map(group => (
          <div key={group.id} className="mb-8">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">
              {group.name}
              <span className="ml-2 text-zinc-600 normal-case">{group.cards.length}</span>
            </h3>
            <div className="card-grid">
              {group.cards.map(card => (
                <PokemonCardTile
                  key={`${card.id}::${card.owned_variant}`}
                  card={card}
                  collectionMap={collection}
                  conditionMap={conditions}
                  onCardClick={onCardClick}
                  onVariantClick={handleVariantClick}
                  cardVariants={[makeVariant(card.owned_variant)]}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
